import { Stack } from '@mui/material';
import { FlowFileConversionFormat, FlowFileConversionOperation } from 'flow-schema';

import { api } from 'common';

import {
  BlockValueTemplateField,
  BlockValueTemplateFieldArea,
} from '../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

interface FileConversionFormFieldsProps {
  fieldNamePrefix?: string;
}

export function FileConversionFormFields(props: FileConversionFormFieldsProps) {
  const { fieldNamePrefix = 'config' } = props;
  const { value: operation = FlowFileConversionOperation.Convert } =
    useSelectedNodeField<FlowFileConversionOperation>(`${fieldNamePrefix}.operation`);
  const { value: format = FlowFileConversionFormat.Base64 } =
    useSelectedNodeField<FlowFileConversionFormat>(`${fieldNamePrefix}.format`);
  const { value: splitMode = api.FlowDefinition.SplitMode.Range } =
    useSelectedNodeField<api.FlowDefinition.SplitMode>(`${fieldNamePrefix}.splitMode`);
  const { setValue: setBase64Payload } = useSelectedNodeField<string | undefined>(
    `${fieldNamePrefix}.base64Payload`
  );
  const { setValue: setFileReference } = useSelectedNodeField<string | undefined>(
    `${fieldNamePrefix}.fileReference`
  );

  return (
    <Stack gap={2}>
      <BlockSelectField
        label="Operation"
        fieldName={`${fieldNamePrefix}.operation`}
        clearable={false}
        options={[
          { label: 'Convert File', value: FlowFileConversionOperation.Convert },
          { label: 'Split PDF', value: FlowFileConversionOperation.SplitPdf },
          {
            label: 'Combine PDFs',
            value: FlowFileConversionOperation.CombinePdf,
          },
          {
            label: 'Extract Pages',
            value: FlowFileConversionOperation.ExtractPages,
          },
        ]}
        defaultValue={FlowFileConversionOperation.Convert}
        helperText="Choose the file operation to perform"
      />

      {operation === FlowFileConversionOperation.Convert && (
        <>
          <BlockSelectField
            label="Format"
            fieldName={`${fieldNamePrefix}.format`}
            clearable={false}
            defaultValue={FlowFileConversionFormat.Base64}
            options={[
              { label: 'Base64', value: FlowFileConversionFormat.Base64 },
              { label: 'PDF', value: FlowFileConversionFormat.Pdf },
              ...(format === FlowFileConversionFormat.Base64ToFile
                ? [
                    {
                      label: 'Base64 to File (deprecated — use Generate File)',
                      value: FlowFileConversionFormat.Base64ToFile,
                    },
                  ]
                : []),
            ]}
            onChange={(event) => {
              const newFormat = event.target.value as FlowFileConversionFormat;
              if (newFormat === FlowFileConversionFormat.Base64ToFile) {
                setFileReference(undefined);
              } else if (
                newFormat === FlowFileConversionFormat.Base64 ||
                newFormat === FlowFileConversionFormat.Pdf
              ) {
                setBase64Payload(undefined);
              }
            }}
            helperText="Choose which format to convert the file to"
          />
          {format === FlowFileConversionFormat.Base64ToFile && (
            <BlockValueTemplateField
              fieldName={`${fieldNamePrefix}.base64Payload`}
              label="Base64 Payload"
              helperText="A base64 encoded string representing the file content"
            />
          )}
          {(format === FlowFileConversionFormat.Base64 ||
            format === FlowFileConversionFormat.Pdf) && (
            <BlockValueTemplateField
              fieldName={`${fieldNamePrefix}.fileReference`}
              label="File reference"
              helperText="A file reference in the format {type}:{identifier} (e.g., flowDocument:abc123). See documentation for all supported types."
            />
          )}
        </>
      )}

      {operation === FlowFileConversionOperation.SplitPdf && (
        <>
          <BlockValueTemplateField
            fieldName={`${fieldNamePrefix}.fileReference`}
            label="PDF File"
            helperText="A file reference to the PDF to split (e.g., flowDocument:abc123)"
          />
          <BlockSelectField
            label="Split by"
            fieldName={`${fieldNamePrefix}.splitMode`}
            clearable={false}
            options={[
              { label: 'Range', value: api.FlowDefinition.SplitMode.Range },
              { label: 'Every X pages', value: api.FlowDefinition.SplitMode.EveryXPages },
            ]}
            defaultValue={api.FlowDefinition.SplitMode.Range}
            helperText="Choose how to split the PDF"
          />
          {splitMode === api.FlowDefinition.SplitMode.Range && (
            <BlockValueTemplateField
              fieldName={`${fieldNamePrefix}.pageRanges`}
              label="Extract Range"
              helperText="Comma-separated page ranges (e.g., '1-5,6-12' or '1,3,5-7'). Each range produces one output file."
            />
          )}
          {splitMode === api.FlowDefinition.SplitMode.EveryXPages && (
            <BlockValueTemplateField
              fieldName={`${fieldNamePrefix}.pageCount`}
              label="Pages per file"
              helperText="Number of pages per output file. A 20-page PDF split every 5 pages produces 4 files."
            />
          )}
        </>
      )}

      {operation === FlowFileConversionOperation.CombinePdf && (
        <BlockValueTemplateFieldArea
          fieldName={`${fieldNamePrefix}.fileReferences`}
          label="File References"
          helperText='A JSON array of file references to combine into a single PDF (e.g. ["flowDocument:abc123", "flowDocument:def456"]). All files must be PDFs.'
          placeholder="e.g. {{myBlock.data.fileReferences}} or ['flowDocument:{id1}', 'flowDocument:{id2}']"
          required
        />
      )}

      {operation === FlowFileConversionOperation.ExtractPages && (
        <>
          <BlockValueTemplateField
            fieldName={`${fieldNamePrefix}.fileReference`}
            label="PDF File"
            helperText="A file reference to the PDF to extract pages from (e.g., flowDocument:abc123)"
          />
          <BlockValueTemplateField
            fieldName={`${fieldNamePrefix}.pageNumbers`}
            label="Page Numbers"
            helperText="Pages to extract as a comma-separated list of individual pages and/or ranges (e.g., '1,2,3,5' or '1,3-5,7'). Produces a single PDF with the specified pages."
          />
        </>
      )}
    </Stack>
  );
}
